import React, { useState, useEffect } from 'react';
import './CartPopup.css';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import CheckoutModal from './CheckoutModal';
import { 
  XMarkIcon, 
  PlusIcon, 
  MinusIcon, 
  TrashIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

interface CartPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartPopup: React.FC<CartPopupProps> = ({ isOpen, onClose }) => {
  const { 
    cartItems, 
    cartCount, 
    cartTotal, 
    shippingTotal,
    grandTotal,
    isLoading,
    updateQuantity, 
    removeFromCart, 
    clearCart 
  } = useCart();
  const { user, openAuthModal } = useAuth();

  const [animationKey, setAnimationKey] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);

  // Reset animation key when cart opens
  useEffect(() => {
    if (isOpen) {
      setAnimationKey(prev => prev + 1);
    }
  }, [isOpen]);

  // Handle quantity updates
  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    try {
      await updateQuantity(itemId, newQuantity);
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  // Handle item removal
  const handleRemoveItem = async (itemId: string) => {
    try {
      await removeFromCart(itemId);
    } catch (error) {
      console.error('Error removing item:', error);
    }
  };

  // Handle cart clearing
  const handleClearCart = async () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      try {
        await clearCart();
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    }
  };

  // Format price to British Pound
  const formatPrice = (price: number) => {
    return `£${price.toFixed(2)}`;
  };

  const handleCheckout = () => {
    if (!user) {
      openAuthModal('login');
      return;
    }
    setShowCheckout(true);
    onClose();
  };

  // Handle successful checkout
  const handleCheckoutSuccess = (orderId: string) => {
    console.log('Order created successfully:', orderId);
    // You can redirect to order confirmation page or show success message
    onClose();
  };

  if (!isOpen && !showCheckout) return null;

  return (
    <>
      {isOpen && (
        <>
      {/* Backdrop */}
      <div className="cart-backdrop" onClick={onClose} />
      
      {/* Cart Popup */}
      <div key={animationKey} className={`cart-popup ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="cart-header">
          <div className="cart-title">
            <ShoppingBagIcon className="cart-title-icon" />
            <h2>Your Cart</h2>
          </div>
          <button className="cart-close-btn" onClick={onClose}>
            <XMarkIcon className="close-icon" />
          </button>
        </div>

        {/* Cart Content */}
        <div className="cart-content">
          {cartItems.length === 0 ? (
            // Empty Cart State
            <div className="empty-cart">
              <ShoppingBagIcon className="empty-cart-icon" />
              <h3>Your cart is empty</h3>
              <p>Add some products to get started!</p>
            </div>
          ) : (
                         // Cart Items List
             <div className="cart-items">
               {cartItems.map((item) => (
                 <div key={item.id} className="cart-item">
                   <div className="cart-item-content">
                     {/* Item Image */}
                     <div className="cart-item-image">
                       <img src={item.image} alt={item.name} />
                     </div>

                     {/* Item Details */}
                     <div className="cart-item-details">
                       <div className="cart-item-main-content">
                         <div className="cart-item-info">
                           <h4 className="cart-item-title">{item.name}</h4>
                           {item.category && (
                             <div className="cart-item-category">
                               <span>Category:</span>
                               <span className="cart-item-category-value">{item.category}</span>
                             </div>
                           )}
                         </div>
                         <div className="cart-item-price">
                           {formatPrice(item.total)}
                         </div>
                       </div>
                       
                       <div className="cart-item-controls">
                         <div className="cart-item-quantity">
                           <button
                             className="quantity-btn quantity-decrease"
                             onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                             disabled={isLoading}
                           >
                             <MinusIcon className="quantity-icon" />
                           </button>
                           <span className="quantity-display">{item.quantity}</span>
                           <button
                             className="quantity-btn quantity-increase"
                             onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                             disabled={isLoading}
                           >
                             <PlusIcon className="quantity-icon" />
                           </button>
                         </div>
                         <button
                           className="cart-item-remove"
                           onClick={() => handleRemoveItem(item.id)}
                           disabled={isLoading}
                           title="Remove item"
                         >
                           <TrashIcon className="remove-icon" />
                         </button>
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
          )}
        </div>

        {/* Cart Footer */}
        {cartItems.length > 0 && (
          <div className="cart-footer">
            {/* Cart Summary */}
            <div className="cart-summary">
              <div className="cart-summary-row">
                <span>Subtotal ({cartCount} items):</span>
                <span className="cart-subtotal">{formatPrice(cartTotal)}</span>
              </div>
              <div className="cart-summary-row">
                <span>Shipping:</span>
                <span className="cart-shipping">{shippingTotal > 0 ? formatPrice(shippingTotal) : 'Free'}</span>
              </div>
              <div className="cart-summary-row cart-total-row">
                <span>Total:</span>
                <span className="cart-total">{formatPrice(grandTotal)}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="cart-actions">
              <button
                className="cart-clear-btn"
                onClick={handleClearCart}
                disabled={isLoading}
              >
                Clear Cart
              </button>
              <button
                className="cart-checkout-btn"
                onClick={handleCheckout}
                disabled={isLoading}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        onSuccess={handleCheckoutSuccess}
      />
    </>
  );
};

export default CartPopup; 